import { Drawer, Theme, useMediaQuery } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import clsx from 'clsx';
import React, { PropsWithChildren, Attributes } from 'react';
import { printStyles } from '../styles/printStyles';
import { enhanceChildrenProps } from '../utils/ChildrenPropsEnhancer';
import * as DrawerSize from './DrawerSize';
import { buildDrawerTransition } from './buildDrawerTransition';

export const DrawerContent = (props: PropsWithChildren<IDrawerProps>): JSX.Element => {
    const { onClose, isOpened, classes: customClasses, logoSrc } = props;

    const classes = useStyles();

    const theme = useTheme();

    const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
    const isTabletOrMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const drawerClass = clsx(classes.drawer, classes.hideForPrint);

    const drawerPaperClass = clsx(classes.drawerPaper, classes.hideForPrint, customClasses && customClasses.drawer);

    const menuItems = enhanceChildrenProps(props, (originalProps: Partial<any> & Attributes) => ({
        ...originalProps,
        onClick: (...args: any[]) => {
            if (originalProps.onClick) {
                originalProps.onClick(args);
            }
            props.onClose();
        }
    }));

    if (isMobile) {
        return (
            <Drawer variant="temporary" open={isOpened} classes={{ paper: drawerPaperClass }} onClose={onClose} className={drawerClass}>
                <img src={logoSrc} className={classes.toolbar} alt="" />
                <div>{menuItems}</div>
            </Drawer>
        );
    } else {
        const isTablet = isTabletOrMobile && !isMobile;
        const isNarrow = isTablet && !isOpened;

        return (
            <Drawer
                variant="permanent"
                open={!isTablet || isOpened}
                classes={{
                    paper: clsx(drawerPaperClass, isNarrow && classes.drawerPaperNarrow)
                }}
                onClose={onClose}
                className={clsx(drawerClass, isNarrow && classes.drawerNarrow)}
            >
                <div className={classes.toolbar} />
                <div>{menuItems}</div>
            </Drawer>
        );
    }
};

export interface IDrawerProps {
    onClose: () => void;
    isOpened: boolean;
    classes?: Partial<Record<'drawer', string>>;
    logoSrc: string;
}

const useStyles = makeStyles((theme: Theme) => {
    const transition = buildDrawerTransition(theme);

    return {
        ...printStyles,
        drawer: {
            width: DrawerSize.wide,
            flexShrink: 0
        },
        drawerNarrow: {
            width: DrawerSize.narrow
        },
        drawerPaper: {
            width: DrawerSize.wide,
            backgroundColor: theme.palette.secondary.dark
        },
        drawerPaperNarrow: {
            [theme.breakpoints.between('sm', 'md')]: {
                overflowX: 'hidden',
                transition,
                width: DrawerSize.narrow
            }
        },
        toolbar: theme.mixins.toolbar
    };
});
